import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Feature, Map } from "ol";
import { Coordinate } from "ol/coordinate";
import MapConstants from "../constants";
import { Fill, Stroke, Style } from "ol/style";
import { Geometry } from "ol/geom";
import { Size } from "ol/size";
import { HttpService } from "src/app/services/http.service";
import { ItemSortHelper } from "src/app/utils/itemSortHelper";
import { FeatureLike } from "ol/Feature";
import { BlastPolygonWKTDto } from "src/app/bo/polygon/tirsPolygone";
import WKT from 'ol/format/WKT';
import { getCenter } from "ol/extent";
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';

proj4.defs('EPSG:31370', '+proj=lcc +lat_0=90 +lon_0=4.36748666666667 +lat_1=49.8333333333333 +lat_2=51.1666666666667 +x_0=150000.013 +y_0=5400088.438 +ellps=intl +towgs84=-106.8686,52.2978,-103.7239,0.3366,-0.457,1.8422,-1.2747 +units=m +no_defs');
 
register(proj4);


export class KpiValue {
    idTir: number;
    valeur: string;
    unite: string;
    couleur?: string;
}

type HSLColor = { h: number; s: number; l: number };

export const PolygonStartColor = { h: 300, s: 80, l: 50 };
export const PoylgonEndColor = { h: 0, s: 80, l: 50 };

// Palette de couleurs pour les zones (HSL)
export const ZoneColorPalette: { [key: number]: string } = {};
let totalZonesCount = 0;
let uniqueZoneIds: number[] = [];

// Fonction pour initialiser les zones uniques et générer les couleurs
function initializeZoneColors(zoneIds: number[]): void {
    uniqueZoneIds = [...new Set(zoneIds)].sort((a, b) => a - b);
    totalZonesCount = uniqueZoneIds.length;
    console.log(`Initializing colors for ${totalZonesCount} unique zones:`, uniqueZoneIds);
    
    // Générer les couleurs en divisant le cercle de teinte
    uniqueZoneIds.forEach((zoneId, index) => {
        if (!ZoneColorPalette[zoneId]) {
            const hue = (360 / totalZonesCount) * index; // Divise le cercle par le nombre de zones
            const saturation = 75; // Saturation fixe à 75%
            const lightness = 55; // Luminosité fixe à 55%
            ZoneColorPalette[zoneId] = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
            console.log(`Zone ${zoneId} assigned color: hsl(${hue}, ${saturation}%, ${lightness}%)`);
        }
    });
}

// Fonction pour générer une couleur unique pour une zone basée sur son ID
function getZoneColor(zoneId: number): string {
    if (!ZoneColorPalette[zoneId]) {
        console.warn(`Zone ${zoneId} not found in palette, generating fallback color`);
        // Fallback: génère une couleur basique si la zone n'a pas été initialisée
        const hue = ((zoneId * 137.508) % 360);
        const saturation = 75;
        const lightness = 55;
        ZoneColorPalette[zoneId] = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    }
    return ZoneColorPalette[zoneId];
}

export class BlastPolygonsLayer {
    private readonly http: HttpService;
    private readonly source: VectorSource;
    private readonly vectorLayer: VectorLayer<VectorSource>;
    private readonly map: Map;

    private minKPIValue: number;
    private maxKPIValue: number;

    private cachedBlastIds: number[] = [];
    private kpis: KpiValue[] = [];
    private visibleBlastIds: number[] = [];
    private blastPolygons: BlastPolygonWKTDto[] = [];
    private siteId: number;
    private blastsData: any[] = []; // Pour stocker les données des tirs avec zones
    private useZoneColors = false; // Flag pour activer/désactiver les couleurs par zones
    private useNeutralColors = false; // Flag pour activer le mode couleurs neutres



    public constructor(map: Map, siteId: number, http: HttpService, zIndex: number) {
        this.http = http;
        this.map = map;
        this.siteId = siteId;
        this.source = new VectorSource();
        this.vectorLayer = new VectorLayer({
            source: this.source,
            style: (feature) => { return this.blastPolygoneStyleFunction(feature); },
        });


        this.vectorLayer.setZIndex(zIndex);
        this.map.addLayer(this.vectorLayer);

    }


    public changeSite(siteId: number) {
        this.source.clear();
        this.siteId = siteId;
        this.cachedBlastIds = [];
        this.blastPolygons = [];
    }

    public clear() {
        this.source.clear();
        this.cachedBlastIds = [];
    }

    public displayPolygonsForBlast(blastIds: number[], types: number[], skipCache = false, styleType: number = null): Promise<void> {
        this.visibleBlastIds = blastIds;

        return new Promise((resolve, reject) => {
            //get holes not already in cache
            let missingIds = skipCache == true ? blastIds : ItemSortHelper.arrayDifference(blastIds, this.cachedBlastIds);

            this.vectorLayer.setVisible(true);

            this.fetchBlastPolygons(missingIds, types).then((response: BlastPolygonWKTDto[]) => {
                console.log('fetch polygons: ', response);
                if (response !== undefined && response !== null && response.length > 0) {
                    this.blastPolygons.push(...response);
                    this.cachedBlastIds.push(...blastIds);
                    if (styleType === 4)
                    {
                        const blastPoly = response.find(poly => poly.type === 1) ?? response.find(poly => poly.type === 0);
                        if (blastPoly) {
                            this.createPolygone(blastPoly, styleType);
                        }
                    }
                    else{
                        response.forEach(blastPoly => {
                            this.createPolygone(blastPoly, styleType);
                        });
                    }
                }

            }
                , (error) => {
                    console.error("error: ", error);
                }).then(() => {
                    this.vectorLayer.changed();
                    resolve();
                });
        });

    }

    public resetPolygonsKPIs() {
        this.kpis = [];
        this.source.getFeatures().forEach((feature) => {
            feature.set(MapConstants.KPI, undefined);
            this.refreshHoverColor(feature);
        });
    }

    public setBlastKpis(kpis: KpiValue[], minValue: number, maxValue: number) {
        this.minKPIValue = minValue;
        this.maxKPIValue = maxValue;
        this.kpis = kpis;

        console.log('minKPIValue:', this.minKPIValue, 'maxKPIValue:', this.maxKPIValue);


        this.source.getFeatures().forEach((feature) => {
            let blastId = feature.get(MapConstants.TIR_ID).value;
            if (blastId !== undefined) {
                let kpi = kpis.find(kpi => kpi.idTir === blastId);
                if (kpi !== undefined) {
                    feature.set(MapConstants.KPI, kpi);
                }
                this.refreshHoverColor(feature);
            }
        });
    }

    // Nouvelle méthode pour définir les données des tirs avec zones
    public setBlastsData(blastsData: any[]): void {
        this.blastsData = blastsData;
        console.log('Blasts data set:', blastsData);
        
        // Extraire tous les zoneIds uniques pour initialiser les couleurs
        const zoneIds = blastsData
            .filter(blast => blast.zoneId && blast.zoneId > 0)
            .map(blast => blast.zoneId);
        
        if (zoneIds.length > 0) {
            initializeZoneColors(zoneIds);
        }
        
        // Vérifier si on a des données avec zoneId
        blastsData.forEach(blast => {
            console.log(`Blast ${blast.tirId} has zoneId: ${blast.zoneId}`);
        });
        
        // Met à jour les features existantes avec les données des zones
        this.source.getFeatures().forEach((feature) => {
            let blastId = feature.get(MapConstants.TIR_ID).value;
            if (blastId !== undefined) {
                let blastData = blastsData.find(blast => blast.tirId === blastId);
                if (blastData) {
                    console.log(`Setting zone data for feature ${blastId}:`, blastData);
                    feature.set('ZONE_DATA', blastData);
                } else {
                    console.warn(`No blast data found for feature ${blastId}`);
                }
                this.refreshHoverColor(feature);
            }
        });
    }

    // Nouvelle méthode pour activer/désactiver les couleurs par zones
    public setZoneColorsEnabled(enabled: boolean): void {
        this.useZoneColors = enabled;
        console.log('Zone colors enabled:', enabled);

        // Force le re-rendu des polygones
        this.source.getFeatures().forEach((feature) => {
            console.log(`Updating feature style for blast ${feature.get(MapConstants.TIR_ID)?.value}`);
            this.refreshHoverColor(feature);
            feature.changed();
        });

        // Également forcer le recalcul de la couche
        this.vectorLayer.changed();
        console.log(`Zone colors ${enabled ? 'enabled' : 'disabled'}, features updated`);
    }

    // Méthode pour activer/désactiver le mode couleurs neutres
    public setNeutralColorsEnabled(enabled: boolean): void {
        this.useNeutralColors = enabled;
        console.log('Neutral colors enabled:', enabled);

        // Force le re-rendu des polygones
        this.source.getFeatures().forEach((feature) => {
            this.refreshHoverColor(feature);
            feature.changed();
        });

        // Également forcer le recalcul de la couche
        this.vectorLayer.changed();
        console.log(`Neutral colors ${enabled ? 'enabled' : 'disabled'}, features updated`);
    }

    // Nouvelle méthode pour obtenir la couleur d'une zone
    private getZoneColor(zoneId: number): string {
        if (zoneId && zoneId > 0) {
            return getZoneColor(zoneId);
        }
        return 'rgba(145, 40, 59, 0.8)'; // Couleur par défaut
    }

    public findPolygoneInView(extent: Size): Feature<Geometry>[] {
        let features = this.source.getFeaturesInExtent(extent);
        return features;
    }

    public hideAll() {
        this.vectorLayer.setVisible(false);
    }

    public filterPolygonsByType(visibleTypes: number[]) {
        console.log('Filtering polygons by types:', visibleTypes);
        
        // Rendre la couche visible si elle était cachée
        this.vectorLayer.setVisible(true);
        
        let debugTypes: { [key: number]: number } = {};
        
        // Parcourir tous les polygones et les masquer/afficher selon le type
        this.source.getFeatures().forEach(feature => {
            const polyType = feature.get(MapConstants.POLYGONE_TYPE);
            
            // Gérer tous les formats possibles de stockage du type
            let typeValue: number;
            if (polyType && typeof polyType === 'object' && polyType.value !== undefined) {
                typeValue = Number(polyType.value);
            } else if (polyType !== undefined && polyType !== null) {
                typeValue = Number(polyType);
            } else {
                typeValue = -1; // Type invalide
            }
            
            // Compter les types pour debug
            debugTypes[typeValue] = (debugTypes[typeValue] || 0) + 1;
            
            // Vérifier si le type est dans la liste des types visibles
            const shouldShow = visibleTypes.some(visibleType => Number(visibleType) === typeValue);
            
            if (shouldShow) {
                // Polygone visible - rétablir le style par défaut
                feature.setStyle(undefined);
            } else {
                // Polygone invisible - style vide
                feature.setStyle(() => []);
            }
        });
        
        console.log('Polygon types found:', debugTypes);
        
        // Forcer le rafraîchissement de la couche
        this.vectorLayer.changed();
    }

    public showSpecyficFeature(f: Feature) {
        this.source.addFeature(f);
    }


    private createPolygone(polygonDto: BlastPolygonWKTDto, styleType: number = null) {
        const wkt = new WKT();
        var polygonFeature = wkt.readFeature(polygonDto.wktPolygon, {
            dataProjection: `EPSG:${polygonDto.epsg}`,
            featureProjection: MapConstants.EPSG_REFERENCE_OL
        });

        polygonFeature.set(MapConstants.FEATURE_NAME, MapConstants.FEATURE_TYPE.blast_polygone);
        polygonFeature.set(MapConstants.TIR_ID, polygonDto.blastId);
        polygonFeature.set(MapConstants.POLYGONE_TYPE, { value: polygonDto.type });
        polygonFeature.set(MapConstants.POLYGONE_STYLE_TYPE, { value: styleType });
        // Set the blastPolygonId for deletion
        polygonFeature.set('blastPolygonId', polygonDto.blastPolygonId);

        // Ajouter les données de zone si disponibles
        if (this.blastsData && this.blastsData.length > 0) {
            let blastData = this.blastsData.find(blast => blast.tirId === polygonDto.blastId);
            if (blastData) {
                console.log(`Adding zone data to feature ${polygonDto.blastId}:`, blastData);
                polygonFeature.set('ZONE_DATA', blastData);
            }
        }
        
        if (this.kpis !== undefined && this.kpis !== null) {
            let kpi = this.kpis.find(kpi => kpi.idTir === polygonDto.blastId);
            if (kpi !== undefined) {
                polygonFeature.set(MapConstants.KPI, kpi);
            }
        }
        let style: Style;
        let color = '';

        this.refreshHoverColor(polygonFeature as Feature<Geometry>);
        this.source.addFeature(polygonFeature);
    }


    private fetchBlastPolygons(blastIds: number[], types: number[]): Promise<any> {
        if (this.siteId !== undefined && this.siteId !== null) {

            if (blastIds.length === 0) {
                console.log("no blastIds to fetch");
                return Promise.resolve([]);
            }
            var body = {
                idsTirs: blastIds,
                idsTirsPolygoneType: types
            };
            return this.http.post('TirsPolygone/' + this.siteId + '/tirsPolygoneByIdTir', body)
                .toPromise();
        }
        else {
            console.warn("siteId is not defined");
        }
    }

    private blastPolygoneStyleFunction(feature: FeatureLike): Style {
        const blastId = feature.get(MapConstants.TIR_ID).value;
        const typeId = feature.get(MapConstants.POLYGONE_TYPE).value;
        const typeStyleId = feature.get(MapConstants.POLYGONE_STYLE_TYPE).value;

        if (!this.isBlastVisible(blastId)) {
            console.log('reset style for blast', blastId);
            return null;
        }

        const color = this.determinePolygonColor(feature, blastId);

        if(typeStyleId === 4){
            return this.createQAQCStyle(typeId);
        }

        return this.createStyleByType(typeId, color);
    }

    private refreshHoverColor(feature: Feature<Geometry>): void {
        const blastId = feature.get(MapConstants.TIR_ID)?.value;
        if (blastId === undefined) return;
        const color = this.determinePolygonColor(feature, blastId);
        feature.set(MapConstants.HOVER_FILL_COLOR, color, true);
    }

    private isBlastVisible(blastId: any): boolean {
        return blastId !== undefined && this.visibleBlastIds.includes(blastId);
    }

    private determinePolygonColor(feature: FeatureLike, blastId: any): string {
        console.log(`Styling blast ${blastId}, useZoneColors: ${this.useZoneColors}, useNeutralColors: ${this.useNeutralColors}`);

        if (this.useNeutralColors) {
            return 'rgba(180, 180, 180, 0.5)'; // Couleur grise neutre
        }

        if (this.useZoneColors) {
            return this.getZoneColorForFeature(feature, blastId);
        }

        return this.getKpiColorForFeature(feature, blastId);
    }

    private getZoneColorForFeature(feature: FeatureLike, blastId: any): string {
        let zoneData = feature.get('ZONE_DATA');
        console.log(`Zone data for blast ${blastId}:`, zoneData);
        
        // Fallback: vérification directe dans blastsData
        if (!zoneData && this.blastsData) {
            zoneData = this.blastsData.find(blast => blast.tirId === blastId);
            console.log(`Found zone data from blastsData for blast ${blastId}:`, zoneData);
        }
        
        if (zoneData && zoneData.zoneId) {
            const color = getZoneColor(zoneData.zoneId);
            console.log(`Zone color for blast ${blastId}, zone ${zoneData.zoneId}:`, color);
            return color;
        }
        
        console.warn(`No zone data found for blast ${blastId}`);
        return 'rgba(145, 40, 59, 0.8)'; // Couleur par défaut
    }

    private getKpiColorForFeature(feature: FeatureLike, blastId: any): string {
        const kpi = feature.get(MapConstants.KPI);
        if (kpi !== undefined) {
            const kpiValue = parseFloat(kpi.valeur);
            const color = this.getKpiColor(kpiValue);
            console.log(`KPI color for blast ${blastId}:`, color);
            return color;
        }
        return 'rgba(145, 40, 59, 0.8)'; // Couleur par défaut
    }

    private createStyleByType(typeId: any, color: string): Style {
        if (typeId == 3) {
            return this.createDangerZoneStyle();
        }
        
        if (typeId == 1) {
            return this.createDrilledStyle(color);
        }

        return this.createDefaultStyle(color);
    }

    private createDangerZoneStyle(): Style {
        return new Style({
            fill: new Fill({
                color: 'rgba(255, 192, 203, 0.6)' // Pink fill with 60% opacity
            }),
            stroke: new Stroke({
                color: 'black',
                width: 1,
                lineDash: [4] // Dotted line style
            }),
            zIndex: 3
        });
    }

    private createDrilledStyle(color: string): Style {
        return new Style({
            fill: new Fill({
                color: color,
            }),
            stroke: new Stroke({
                color: 'rgba(200, 0, 0, 0.5)',
                width: 2,
            }),
            zIndex: 4
        });
    }

    private createQAQCStyle(typeStyle: any = null): Style {
        if(typeStyle == 0){
            return new Style({
                fill: new Fill({
                    color: 'rgba(255, 255, 255, 0)',
                }),
                stroke: new Stroke({
                    color: 'rgba(41, 40, 40, 0.9)',
                    width: 4,
                    lineDash: [5, 5]
                }),
                zIndex: 4
            });
        }
        else{
            return new Style({
                fill: new Fill({
                    color: 'rgba(255, 255, 255, 0)',
                }),
                stroke: new Stroke({
                    color: 'rgba(41, 40, 40, 0.9)',
                    width: 4,
                }),
                zIndex: 4
            });
        }
        
    }

    private createDefaultStyle(color: string): Style {
        return new Style({
            fill: new Fill({
                color: color,
            }),
            stroke: new Stroke({
                color: 'rgba(0, 0, 0, 0.5)',
                width: 2,
            }),
            zIndex: 4
        });
    }

    private getKpiColor(kpiValue: number): string {
        // Ensure the value is within the range
        kpiValue = Math.min(Math.max(kpiValue, this.minKPIValue), this.maxKPIValue);
        const ratio = (kpiValue - this.minKPIValue) / (this.maxKPIValue - this.minKPIValue);
        if (isNaN(ratio) || ratio < 0 || ratio > 1) {
            return 'rgba(255, 255, 255, 0.8)';
        }
        const h = PolygonStartColor.h + (PoylgonEndColor.h - PolygonStartColor.h) * ratio;
        return `hsl(${h}, ${PolygonStartColor.s}%, ${PolygonStartColor.l}%, 0.8)`;
    }

    public getPolygonCenter(tirId: number): Coordinate | null {
        const features = this.source.getFeatures();
        
        const feature = features.find(f => {
            const tirIdObj = f.get(MapConstants.TIR_ID);
            return tirIdObj && (tirIdObj.value === tirId || tirIdObj === tirId);
        });
        
        if (feature) {
            const geometry = feature.getGeometry();
            if (geometry) {
                return getCenter(geometry.getExtent());
            }
        }
        
        return null;
    }
}


let BlastStyle = new Style({
    fill: new Fill({
        color: 'rgba(145, 40, 59, 0.8)',
    }),
    stroke: new Stroke({
        color: 'rgba(0, 0, 0, 0.5)',

        width: 2,
    }),

});